'use client';
import React, { useState } from 'react';
import { Search, MoreVertical, UserX, Lock, Pause, Eye, ChevronDown, CheckCircle, XCircle, Download, RefreshCw, Shield, Mail, Phone } from 'lucide-react';

interface User {
  id: string;
  name: string;
  email: string;
  phone: string;
  country: string;
  status: 'active' | 'suspended' | 'banned' | 'frozen';
  kyc: 'verified' | 'pending' | 'rejected' | 'none';
  balance: string;
  joined: string;
  lastActive: string;
  fraudScore: number;
}

const mockUsers: User[] = [
  { id: 'USR-48291', name: 'Arjun Kumar', email: 'arjun@example.com', phone: '+91 98765 43210', country: 'India', status: 'active', kyc: 'verified', balance: '$124.50', joined: 'Jan 12, 2025', lastActive: '2m ago', fraudScore: 12 },
  { id: 'USR-31204', name: 'Sarah Mitchell', email: 'sarah@example.com', phone: '+1 555 234 5678', country: 'USA', status: 'active', kyc: 'verified', balance: '$890.00', joined: 'Mar 5, 2025', lastActive: '1h ago', fraudScore: 8 },
  { id: 'USR-22891', name: 'Carlos Mendez', email: 'carlos@example.com', phone: '+34 612 345 678', country: 'Spain', status: 'suspended', kyc: 'pending', balance: '$45.20', joined: 'Feb 18, 2025', lastActive: '3d ago', fraudScore: 67 },
  { id: 'USR-19034', name: 'Priya Sharma', email: 'priya@example.com', phone: '+91 87654 32109', country: 'India', status: 'active', kyc: 'verified', balance: '$312.80', joined: 'Apr 2, 2025', lastActive: '5m ago', fraudScore: 5 },
  { id: 'USR-15892', name: 'James Wilson', email: 'james@example.com', phone: '+44 7700 900123', country: 'UK', status: 'frozen', kyc: 'verified', balance: '$2,100.00', joined: 'Dec 20, 2024', lastActive: '2d ago', fraudScore: 82 },
  { id: 'USR-12034', name: 'Fatima Al-Hassan', email: 'fatima@example.com', phone: '+971 50 123 4567', country: 'UAE', status: 'active', kyc: 'none', balance: '$0.00', joined: 'Jun 10, 2025', lastActive: '30m ago', fraudScore: 15 },
  { id: 'USR-09821', name: 'Liu Wei', email: 'liu@example.com', phone: '+86 138 0013 8000', country: 'China', status: 'banned', kyc: 'rejected', balance: '$0.00', joined: 'Nov 3, 2024', lastActive: '30d ago', fraudScore: 95 },
  { id: 'USR-07412', name: 'Emma Thompson', email: 'emma@example.com', phone: '+61 400 123 456', country: 'Australia', status: 'active', kyc: 'verified', balance: '$567.30', joined: 'May 14, 2025', lastActive: '10m ago', fraudScore: 3 },
];

const statusConfig = {
  active: { label: 'Active', color: '#10B981', bg: 'rgba(16,185,129,0.12)', icon: CheckCircle },
  suspended: { label: 'Suspended', color: '#F59E0B', bg: 'rgba(245,158,11,0.12)', icon: Pause },
  banned: { label: 'Banned', color: '#F43F5E', bg: 'rgba(244,63,94,0.12)', icon: XCircle },
  frozen: { label: 'Frozen', color: '#06B6D4', bg: 'rgba(6,182,212,0.12)', icon: Lock },
};

const kycConfig = {
  verified: { label: 'Verified', color: '#10B981' },
  pending: { label: 'Pending', color: '#F59E0B' },
  rejected: { label: 'Rejected', color: '#F43F5E' },
  none: { label: 'None', color: '#64748B' },
};

export default function AdminUserManagement() {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [kycFilter, setKycFilter] = useState('all');
  const [actionMenu, setActionMenu] = useState<string | null>(null);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);

  const filtered = mockUsers.filter((u) => {
    const matchSearch = !search || u.name.toLowerCase().includes(search.toLowerCase()) ||
      u.email.toLowerCase().includes(search.toLowerCase()) || u.id.toLowerCase().includes(search.toLowerCase());
    const matchStatus = statusFilter === 'all' || u.status === statusFilter;
    const matchKyc = kycFilter === 'all' || u.kyc === kycFilter;
    return matchSearch && matchStatus && matchKyc;
  });

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">User Management</h1>
          <p className="text-sm mt-0.5" style={{ color: 'var(--muted-foreground)' }}>
            Search, manage, and moderate platform users
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button className="btn-ghost text-xs py-2 px-3 flex items-center gap-1.5">
            <Download size={14} /> Export
          </button>
          <button className="btn-ghost text-xs py-2 px-3 flex items-center gap-1.5">
            <RefreshCw size={14} /> Refresh
          </button>
        </div>
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: 'Total Users', value: '48,291', color: '#7C3AED' },
          { label: 'Active', value: '44,102', color: '#10B981' },
          { label: 'Suspended', value: '1,847', color: '#F59E0B' },
          { label: 'Banned', value: '342', color: '#F43F5E' },
        ].map((s) => (
          <div key={s.label} className="rounded-xl p-4" style={{ background: 'var(--card)', border: '1px solid var(--border)' }}>
            <p className="text-xl font-bold" style={{ color: s.color }}>{s.value}</p>
            <p className="text-xs mt-0.5" style={{ color: 'var(--muted-foreground)' }}>{s.label}</p>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 p-4 rounded-xl" style={{ background: 'var(--card)', border: '1px solid var(--border)' }}>
        <div className="relative flex-1 min-w-48">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--muted-foreground)' }} />
          <input
            type="text"
            placeholder="Search by name, email, ID, phone..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-4 py-2 text-sm rounded-lg outline-none"
            style={{ background: 'var(--muted)', border: '1px solid var(--border)', color: 'var(--foreground)' }}
          />
        </div>
        <div className="relative">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="appearance-none pl-3 pr-8 py-2 text-sm rounded-lg outline-none cursor-pointer"
            style={{ background: 'var(--muted)', border: '1px solid var(--border)', color: 'var(--foreground)' }}
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="suspended">Suspended</option>
            <option value="banned">Banned</option>
            <option value="frozen">Frozen</option>
          </select>
          <ChevronDown size={12} className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: 'var(--muted-foreground)' }} />
        </div>
        <div className="relative">
          <select
            value={kycFilter}
            onChange={(e) => setKycFilter(e.target.value)}
            className="appearance-none pl-3 pr-8 py-2 text-sm rounded-lg outline-none cursor-pointer"
            style={{ background: 'var(--muted)', border: '1px solid var(--border)', color: 'var(--foreground)' }}
          >
            <option value="all">All KYC</option>
            <option value="verified">Verified</option>
            <option value="pending">Pending</option>
            <option value="rejected">Rejected</option>
            <option value="none">None</option>
          </select>
          <ChevronDown size={12} className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: 'var(--muted-foreground)' }} />
        </div>
        <span className="text-xs ml-auto" style={{ color: 'var(--muted-foreground)' }}>{filtered.length} users found</span>
      </div>

      {/* Table */}
      <div className="rounded-xl overflow-hidden" style={{ background: 'var(--card)', border: '1px solid var(--border)' }}>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border)', background: 'var(--muted)' }}>
                {['User', 'Contact', 'Country', 'Status', 'KYC', 'Balance', 'Fraud Score', 'Last Active', 'Actions'].map((h) => (
                  <th key={h} className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider"
                    style={{ color: 'var(--muted-foreground)' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((user) => {
                const sc = statusConfig[user.status];
                const kc = kycConfig[user.kyc];
                return (
                  <tr key={user.id} className="border-b hover:bg-muted/50 transition-colors"
                    style={{ borderColor: 'var(--border)' }}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0"
                          style={{ background: 'linear-gradient(135deg, #7C3AED, #06B6D4)', color: '#fff' }}>
                          {user.name.split(' ').map(n => n[0]).join('').slice(0, 2)}
                        </div>
                        <div>
                          <p className="font-medium text-foreground text-xs">{user.name}</p>
                          <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>{user.id}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="space-y-0.5">
                        <div className="flex items-center gap-1 text-xs" style={{ color: 'var(--muted-foreground)' }}>
                          <Mail size={10} />{user.email}
                        </div>
                        <div className="flex items-center gap-1 text-xs" style={{ color: 'var(--muted-foreground)' }}>
                          <Phone size={10} />{user.phone}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-xs text-foreground">{user.country}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full font-semibold"
                        style={{ background: sc.bg, color: sc.color }}>
                        <sc.icon size={10} />{sc.label}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-xs font-semibold" style={{ color: kc.color }}>{kc.label}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-xs font-semibold text-foreground">{user.balance}</span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="flex-1 h-1.5 rounded-full overflow-hidden" style={{ background: 'var(--border)', width: 48 }}>
                          <div className="h-full rounded-full" style={{
                            width: `${user.fraudScore}%`,
                            background: user.fraudScore > 70 ? '#F43F5E' : user.fraudScore > 40 ? '#F59E0B' : '#10B981'
                          }} />
                        </div>
                        <span className="text-xs font-semibold" style={{
                          color: user.fraudScore > 70 ? '#F43F5E' : user.fraudScore > 40 ? '#F59E0B' : '#10B981'
                        }}>{user.fraudScore}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-xs" style={{ color: 'var(--muted-foreground)' }}>{user.lastActive}</span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="relative">
                        <button
                          onClick={() => setActionMenu(actionMenu === user.id ? null : user.id)}
                          className="p-1.5 rounded-lg hover:bg-muted transition-colors"
                        >
                          <MoreVertical size={14} style={{ color: 'var(--muted-foreground)' }} />
                        </button>
                        {actionMenu === user.id && (
                          <div className="absolute right-0 top-full mt-1 w-44 rounded-xl z-50 overflow-hidden"
                            style={{ background: 'var(--card)', border: '1px solid var(--border)', boxShadow: '0 8px 32px rgba(0,0,0,0.4)' }}>
                            {[
                              { label: 'View Profile', icon: Eye, color: '#A78BFA' },
                              { label: 'Suspend User', icon: Pause, color: '#F59E0B' },
                              { label: 'Freeze Wallet', icon: Lock, color: '#06B6D4' },
                              { label: 'Ban User', icon: UserX, color: '#F43F5E' },
                              { label: 'Request KYC', icon: Shield, color: '#10B981' },
                            ].map((action) => (
                              <button
                                key={action.label}
                                onClick={() => setActionMenu(null)}
                                className="w-full flex items-center gap-2 px-3 py-2.5 text-xs hover:bg-muted transition-colors text-left"
                                style={{ color: action.color }}
                              >
                                <action.icon size={13} />{action.label}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {/* Pagination */}
        <div className="flex items-center justify-between px-4 py-3 border-t" style={{ borderColor: 'var(--border)' }}>
          <span className="text-xs" style={{ color: 'var(--muted-foreground)' }}>Showing {filtered.length} of 48,291 users</span>
          <div className="flex items-center gap-1">
            {[1, 2, 3, '...', 48].map((p, i) => (
              <button key={`page-${i}`}
                className="w-7 h-7 rounded-md text-xs font-medium transition-colors"
                style={{
                  background: p === 1 ? '#7C3AED' : 'var(--muted)',
                  color: p === 1 ? '#fff' : 'var(--muted-foreground)'
                }}>
                {p}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
